import axios from 'axios';
import { API_BASE_URL } from '../config';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useRef, useState, useCallback, useEffect, Fragment } from 'react';

// ─── Type badge colour mapping ────────────────────────────────────────────────
const TYPE_COLORS = {
    'Erreur Site': { bg: '#e74c3c', text: '#fff' },
    'Erreur PHP': { bg: '#8e44ad', text: '#fff' },
    'Structure DOM': { bg: '#e67e22', text: '#fff' },
    'Contenu': { bg: '#f39c12', text: '#fff' },
    'Image cassée': { bg: '#c0392b', text: '#fff' },
    'URL vers visu': { bg: '#2980b9', text: '#fff' },
    'Meta SEO': { bg: '#16a085', text: '#fff' },
    'Erreur Sitemap': { bg: '#7f8c8d', text: '#fff' },
    'Erreur Analyse': { bg: '#34495e', text: '#fff' },
    'Site Inaccessible': { bg: '#ff0000', text: '#fff', border: '2px solid #000' },
    'Erreur': { bg: '#c0392b', text: '#fff' },
    'DS Store présent': { bg: '#6366f1', text: '#fff' },
};

function getTypeColor(type) {
    return TYPE_COLORS[type] || { bg: '#555', text: '#fff' };
}

function formatDate(dateStr) {
    if (!dateStr) return '—';
    try {
        return new Intl.DateTimeFormat('fr-CH', {
            day: '2-digit', month: '2-digit', year: 'numeric',
            hour: '2-digit', minute: '2-digit',
        }).format(new Date(dateStr));
    } catch { return dateStr; }
}

function formatDuration(ms) {
    if (!ms || ms <= 0) return null;
    const totalSec = Math.round(ms / 1000);
    const h = Math.floor(totalSec / 3600);
    const m = Math.floor((totalSec % 3600) / 60);
    const s = totalSec % 60;
    if (h > 0) return `${h}h ${m}m`;
    if (m > 0) return `${m}m ${s}s`;
    return `${s}s`;
}

function getEstimatedRemaining(scanProgress) {
    if (!scanProgress || !scanProgress.startTime || scanProgress.completedSites < 2) return null;
    const elapsed = Date.now() - new Date(scanProgress.startTime).getTime();
    const avgPerSite = elapsed / scanProgress.completedSites;
    const remaining = (scanProgress.totalSites - scanProgress.completedSites) * avgPerSite;
    return formatDuration(remaining);
}

function getNextScanTime() {
    const now = new Date();
    const hours = now.getHours();
    // Cron is 0 */6 * * * -> 0, 6, 12, 18h
    const nextHour = (Math.floor(hours / 6) + 1) * 6;
    const nextDate = new Date(now);
    nextDate.setHours(nextHour % 24, 0, 0, 0);
    if (nextHour >= 24) {
        nextDate.setDate(nextDate.getDate() + 1);
    }

    const diffMs = nextDate - now;
    const diffMin = Math.floor(diffMs / (1000 * 60));
    const h = Math.floor(diffMin / 60);
    const m = diffMin % 60;

    if (h > 0) return `${h}h ${m}m`;
    return `${m}m`;
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function ErrorTypeBadge({ type }) {
    const { bg, text } = getTypeColor(type);
    return (
        <span style={{
            display: 'inline-block',
            background: bg,
            color: text,
            fontSize: '11px',
            fontWeight: 700,
            padding: '2px 8px',
            borderRadius: '4px',
            whiteSpace: 'nowrap',
            border: TYPE_COLORS[type]?.border || 'none'
        }}>
            {type}
        </span>
    );
}

function SiteAccordion({ site, onValidate }) {
    const [open, setOpen] = useState(false);

    const errorsByType = site.urls.reduce((acc, u) => {
        acc[u.type] = (acc[u.type] || 0) + 1;
        return acc;
    }, {});

    const summaryParts = Object.entries(errorsByType)
        .map(([type, count]) => `${type} (${count})`);

    return (
        <div style={{
            border: '1px solid #e2e8f0',
            borderRadius: '10px',
            marginBottom: '10px',
            overflow: 'hidden',
            boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
        }}>
            {/* Header */}
            <button
                onClick={() => setOpen(o => !o)}
                style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '14px 18px',
                    background: open ? '#f8faff' : '#fff',
                    border: 'none',
                    cursor: 'pointer',
                    textAlign: 'left',
                    gap: '12px',
                    transition: 'background 0.2s',
                }}
            >
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                    <i className="fa-regular fa-globe" style={{ color: '#3b82f6', fontSize: '15px' }} />
                    <span style={{ fontWeight: 700, fontSize: '15px', color: '#1e293b' }}>
                        {site.website}
                    </span>
                    <span style={{ fontSize: '12px', color: '#64748b' }}>
                        — {summaryParts.join(', ')}
                    </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{
                        background: '#e74c3c',
                        color: '#fff',
                        borderRadius: '20px',
                        fontSize: '12px',
                        fontWeight: 700,
                        padding: '2px 10px',
                        minWidth: '28px',
                        textAlign: 'center',
                    }}>
                        {site.urls.length}
                    </span>
                    <i className={`fa-solid fa-chevron-${open ? 'up' : 'down'}`}
                        style={{ color: '#94a3b8', fontSize: '12px' }} />
                </div>
            </button>

            {/* Body */}
            {open && (
                <div style={{ padding: '0 0 8px 0' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                        <thead>
                            <tr style={{ background: '#f1f5f9' }}>
                                <th style={thStyle}>#</th>
                                <th style={thStyle}>URL</th>
                                <th style={thStyle}>Type</th>
                                <th style={thStyle}>Message</th>
                                <th style={thStyle}>Date</th>
                                <th style={thStyle}>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {site.urls.map((urlObj, i) => (
                                <tr key={urlObj.id} style={{
                                    background: i % 2 === 0 ? '#fff' : '#f8fafc',
                                    transition: 'background 0.15s',
                                }}>
                                    <td style={tdStyle}>{urlObj.id}</td>
                                    <td style={{ ...tdStyle, maxWidth: '220px', wordBreak: 'break-all' }}>
                                        <a href={urlObj.url} target="_blank" rel="noreferrer"
                                            style={{ color: '#3b82f6', textDecoration: 'none' }}>
                                            {urlObj.url}
                                        </a>
                                    </td>
                                    <td style={tdStyle}>
                                        <ErrorTypeBadge type={urlObj.type} />
                                    </td>
                                    <td style={{ ...tdStyle, maxWidth: '280px', color: '#374151' }}
                                        dangerouslySetInnerHTML={{ __html: urlObj.message }} />
                                    <td style={{ ...tdStyle, whiteSpace: 'nowrap', color: '#64748b' }}>
                                        {formatDate(urlObj.date)}
                                    </td>
                                    <td style={tdStyle}>
                                        <button
                                            onClick={() => onValidate(urlObj.id)}
                                            style={{
                                                background: 'linear-gradient(135deg,#22c55e,#16a34a)',
                                                color: '#fff',
                                                border: 'none',
                                                borderRadius: '6px',
                                                padding: '5px 12px',
                                                fontSize: '12px',
                                                fontWeight: 600,
                                                cursor: 'pointer',
                                                transition: 'opacity 0.2s',
                                            }}
                                            onMouseEnter={e => e.currentTarget.style.opacity = '0.85'}
                                            onMouseLeave={e => e.currentTarget.style.opacity = '1'}
                                        >
                                            ✓ Valider
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

const thStyle = {
    padding: '10px 14px',
    textAlign: 'left',
    fontWeight: 600,
    color: '#475569',
    fontSize: '12px',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    borderBottom: '1px solid #e2e8f0',
};

const tdStyle = {
    padding: '10px 14px',
    borderBottom: '1px solid #f1f5f9',
    verticalAlign: 'top',
};

// ─── Main Component ───────────────────────────────────────────────────────────

const SitesGestion = () => {
    const [data, setData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [scanRunning, setScanRunning] = useState(false);
    const [lastScan, setLastScan] = useState(null);
    const [error, setError] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedType, setSelectedType] = useState('');
    const [checkUrl, setCheckUrl] = useState('');
    const [checkResult, setCheckResult] = useState(null);
    const [checkLoading, setCheckLoading] = useState(false);
    const [scanToast, setScanToast] = useState(null);
    const [scanProgress, setScanProgress] = useState(null);
    const [nextScanIn, setNextScanIn] = useState(getNextScanTime());
    const prevActiveRef = useRef(false);

    // All error types available for the filter
    const allTypes = [...new Set(data.flatMap(s => s.urls.map(u => u.type)))].sort();

    // Fetch errors from API
    const fetchErrors = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await axios.get(`${API_BASE_URL}/xerror/errors`);
            setData(res.data.data || []);
        } catch (e) {
            setError('Impossible de charger les erreurs. Vérifiez la connexion au serveur.');
        } finally {
            setLoading(false);
        }
    }, []);

    // Fetch last scan timestamp
    const fetchLastScan = useCallback(async () => {
        try {
            const res = await axios.get(`${API_BASE_URL}/xerror/last-scan`);
            setLastScan(res.data.last_scan);
        } catch { /* silent */ }
    }, []);

    useEffect(() => {
        document.title = 'Gestion des sites';
        fetchErrors();
        fetchLastScan();

        // Update countdown every minute
        const t = setInterval(() => setNextScanIn(getNextScanTime()), 60000);

        // Check if a scan is already running when mounting
        const checkInitialStatus = async () => {
            try {
                const res = await axios.get(`${API_BASE_URL}/xerror/status`);
                if (res.data.active) {
                    setScanRunning(true);
                    setScanProgress(res.data);
                }
            } catch { /* silent */ }
        };
        checkInitialStatus();
    }, [fetchErrors, fetchLastScan]);

    // Polling for scan progress
    useEffect(() => {
        let interval;
        if (scanRunning || (scanProgress && scanProgress.active)) {
            interval = setInterval(async () => {
                try {
                    const res = await axios.get(`${API_BASE_URL}/xerror/status`);
                    setScanProgress(res.data);
                    if (!res.data.active) {
                        clearInterval(interval);
                        setScanRunning(false);
                        fetchErrors();
                        fetchLastScan();
                    }
                } catch { /* silent */ }
            }, 10000);
        }
        return () => clearInterval(interval);
    }, [scanRunning, scanProgress, fetchErrors, fetchLastScan]);

    // Toast when scan finishes
    useEffect(() => {
        if (scanProgress && !scanProgress.active && prevActiveRef.current) {
            toast.success("Scan terminé");
        }
        if (scanProgress) {
            prevActiveRef.current = scanProgress.active;
        }
    }, [scanProgress]);

    // Apply filters whenever data / filters change
    useEffect(() => {
        const q = searchQuery.toLowerCase();
        const filtered = data
            .map(site => ({
                ...site,
                urls: site.urls.filter(u => {
                    const matchType = selectedType ? u.type === selectedType : true;
                    const matchSearch = q
                        ? (u.url.toLowerCase().includes(q) ||
                            u.message.toLowerCase().includes(q) ||
                            u.type.toLowerCase().includes(q) ||
                            site.website.toLowerCase().includes(q))
                        : true;
                    return matchType && matchSearch;
                }),
            }))
            .filter(site => site.urls.length > 0);
        setFilteredData(filtered);
    }, [data, searchQuery, selectedType]);

    // Validate a single error
    const handleValidate = async (id) => {
        try {
            await axios.post(`${API_BASE_URL}/xerror/validate`, { id });
            setData(prev =>
                prev
                    .map(site => ({ ...site, urls: site.urls.filter(u => u.id !== id) }))
                    .filter(site => site.urls.length > 0)
            );
        } catch {
            alert('Erreur lors de la validation.');
        }
    };

    // Trigger a manual scan
    const handleTriggerScan = async () => {
        setScanRunning(true);
        setScanToast(null);
        setScanProgress(null); // Reset progress UI
        try {
            await axios.post(`${API_BASE_URL}/xerror/trigger-scan`);
            // Initial poll to set active state
            const statusRes = await axios.get(`${API_BASE_URL}/xerror/status`);
            setScanProgress(statusRes.data);
        } catch (err) {
            const msg = err.response?.data?.message || 'Impossible de lancer le scan.';
            setScanToast({ type: 'error', msg });
            setScanRunning(false);
        }
    };

    // Check a custom URL
    const handleCheckUrl = async (e) => {
        e.preventDefault();
        if (!checkUrl.trim()) return;
        setCheckLoading(true);
        setCheckResult(null);
        try {
            const res = await axios.post(`${API_BASE_URL}/xerror/check-page`, { url: checkUrl });
            setCheckResult(res.data.result);
        } catch (err) {
            setCheckResult({ status: 'error', errors: [err.response?.data?.message || 'Erreur réseau'] });
        } finally {
            setCheckLoading(false);
        }
    };

    const totalErrors = filteredData.reduce((n, s) => n + s.urls.length, 0);

    return (
        <Fragment>
            <ToastContainer position="bottom-right" autoClose={3000} />
            <style>{`
                .sg-card {
                    background: #fff;
                    border-radius: 14px;
                    box-shadow: 0 2px 12px rgba(0,0,0,0.07);
                    padding: 22px 24px;
                    margin-bottom: 22px;
                }
                .sg-stat-card {
                    background: linear-gradient(135deg, #3b82f6, #2563eb);
                    color: #fff;
                    border-radius: 14px;
                    padding: 18px 22px;
                    display: flex;
                    flex-direction: column;
                    gap: 4px;
                    flex: 1;
                    min-width: 140px;
                }
                .sg-stat-card.red   { background: linear-gradient(135deg, #ef4444, #dc2626); }
                .sg-stat-card.green { background: linear-gradient(135deg, #22c55e, #16a34a); }
                .sg-stat-card.purple { background: linear-gradient(135deg, #a855f7, #7c3aed); }
                .sg-filter-input {
                    border: 1.5px solid #e2e8f0;
                    border-radius: 8px;
                    padding: 9px 14px;
                    font-size: 14px;
                    outline: none;
                    background: #f8fafc;
                    transition: border-color 0.2s;
                    color: #1e293b;
                }
                .sg-filter-input:focus { border-color: #3b82f6; background: #fff; }
                .sg-btn-primary {
                    background: linear-gradient(135deg, #3b82f6, #2563eb);
                    color: #fff;
                    border: none;
                    border-radius: 9px;
                    padding: 10px 20px;
                    font-size: 14px;
                    font-weight: 600;
                    cursor: pointer;
                    display: inline-flex;
                    align-items: center;
                    gap: 8px;
                    transition: opacity 0.2s, transform 0.15s;
                }
                .sg-btn-primary:hover:not(:disabled) { opacity: 0.88; transform: translateY(-1px); }
                .sg-btn-primary:disabled { opacity: 0.55; cursor: not-allowed; }
                .sg-btn-secondary {
                    background: #f1f5f9;
                    color: #374151;
                    border: 1.5px solid #e2e8f0;
                    border-radius: 9px;
                    padding: 9px 18px;
                    font-size: 14px;
                    font-weight: 500;
                    cursor: pointer;
                    transition: background 0.2s;
                }
                .sg-btn-secondary:hover { background: #e2e8f0; }
                .sg-toast-success {
                    background: #d1fae5; color: #065f46;
                    padding: 10px 16px; border-radius: 8px;
                    font-size: 13px; border: 1px solid #6ee7b7;
                    margin-top: 10px;
                }
                .sg-toast-error {
                    background: #fee2e2; color: #991b1b;
                    padding: 10px 16px; border-radius: 8px;
                    font-size: 13px; border: 1px solid #fca5a5;
                    margin-top: 10px;
                }
                .sg-cron-badge {
                    display: inline-flex; align-items: center; gap: 6px;
                    background: #ecfdf5; color: #065f46;
                    border: 1px solid #6ee7b7;
                    border-radius: 20px;
                    padding: 4px 12px;
                    font-size: 12px;
                    font-weight: 600;
                }
                .sg-pulse { animation: sgpulse 2s infinite; }
                @keyframes sgpulse {
                    0%, 100% { opacity: 1; } 50% { opacity: 0.4; }
                }
            `}</style>

            <div>
                <div style={{ flexDirection: 'column', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px', marginBottom: '24px' }}>
                    <h2 style={{ margin: 0, fontSize: '22px', fontWeight: 800, color: '#0f172a' }}>
                        <i className="fa-solid fa-shield-halved" style={{ color: '#e74c3c', marginRight: '10px' }} />
                        Gestion des sites
                    </h2>
                    <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#64748b' }}>
                        Monitoring automatisé du parc Solution Digitale SA — cron toutes les 6h, durée ~4h
                    </p>
                </div>
                {/* ── Header ── */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'end', flexWrap: 'wrap', gap: '12px', marginBottom: '24px' }}>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexDirection: 'column' }}>

                        <span className="sg-cron-badge">
                            <span className="sg-pulse" style={{ width: '7px', height: '7px', background: '#22c55e', borderRadius: '50%', display: 'inline-block' }} />
                            Cron toutes les 6h — durée ~4h
                        </span>
                        {lastScan && (
                            <span style={{ fontSize: '12px', color: '#94a3b8' }}>
                                Dernier scan : {formatDate(lastScan)}
                            </span>
                        )}
                        <span style={{ fontSize: '11px', color: '#64748b', fontWeight: 500 }}>
                            <i className="fa-regular fa-clock" style={{ marginRight: '5px' }} />
                            Prochain scan automatique dans <strong>{nextScanIn}</strong>
                        </span>

                    </div>
                </div>

                {/* ── Stats ── */}
                <div style={{ display: 'flex', gap: '14px', flexWrap: 'wrap', marginBottom: '22px' }}>
                    <div className="sg-stat-card red">
                        <span style={{ fontSize: '28px', fontWeight: 800 }}>
                            {totalErrors}
                        </span>
                        <span style={{ fontSize: '12px', opacity: 0.85 }}>Erreurs actives</span>
                    </div>
                    <div className="sg-stat-card">
                        <span style={{ fontSize: '28px', fontWeight: 800 }}>{filteredData.length}</span>
                        <span style={{ fontSize: '12px', opacity: 0.85 }}>Sites concernés</span>
                    </div>
                    <div className="sg-stat-card purple">
                        <span style={{ fontSize: '28px', fontWeight: 800 }}>
                            {[...new Set(data.flatMap(s => s.urls.map(u => u.type)))].length}
                        </span>
                        <span style={{ fontSize: '12px', opacity: 0.85 }}>Types d'erreurs distincts</span>
                    </div>
                    <div className="sg-stat-card green">
                        <span style={{ fontSize: '28px', fontWeight: 800 }}>~4h</span>
                        <span style={{ fontSize: '12px', opacity: 0.85 }}>Durée d'un scan</span>
                    </div>
                </div>

                {/* ── Progress Bar ── */}
                {scanProgress && scanProgress.active && (
                    <div className="sg-card" style={{ borderLeft: '5px solid #3b82f6' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                            <h4 style={{ margin: 0, fontSize: '14px', fontWeight: 700, color: '#1e293b' }}>
                                <i className="fa-solid fa-spinner fa-spin" style={{ marginRight: '10px', color: '#3b82f6' }} />
                                Scan en cours...
                            </h4>
                            <span style={{ fontSize: '12px', fontWeight: 600, color: '#64748b' }}>
                                {Math.round((scanProgress.completedSites / (scanProgress.totalSites || 1)) * 100)}%
                            </span>
                        </div>

                        {/* Progress Bar Container */}
                        <div style={{ width: '100%', height: '8px', background: '#e2e8f0', borderRadius: '4px', overflow: 'hidden', marginBottom: '12px' }}>
                            <div style={{
                                width: `${(scanProgress.completedSites / (scanProgress.totalSites || 1)) * 100}%`,
                                height: '100%',
                                background: 'linear-gradient(90deg, #3b82f6, #60a5fa)',
                                transition: 'width 0.4s ease-out'
                            }} />
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '15px' }}>
                            <div style={{ fontSize: '12px', color: '#475569' }}>
                                <strong>Hébergements trouvés :</strong> {scanProgress.completedServers} / {scanProgress.totalServers}
                            </div>
                            <div style={{ fontSize: '12px', color: '#475569' }}>
                                <strong>Sites scannés :</strong> {scanProgress.completedSites} / {scanProgress.totalSites}
                            </div>
                            <div style={{ fontSize: '12px', color: '#475569' }}>
                                <strong>Pages scannées :</strong> {scanProgress.completedPages} {scanProgress.totalPages > 0 ? `/ ${scanProgress.totalPages}` : ''}
                            </div>
                        </div>
                        <div style={{ marginTop: '10px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' }}>
                            {scanProgress.currentSite && (
                                <div style={{ fontSize: '12px', color: '#64748b', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <i className="fa-solid fa-spinner fa-spin" style={{ color: '#3b82f6', fontSize: '11px' }} />
                                    <span>
                                        En cours : <strong style={{ color: '#1e293b' }}>{scanProgress.currentSite}</strong>
                                        {scanProgress.currentServer && (
                                            <span style={{ marginLeft: '6px', color: '#94a3b8' }}>— {scanProgress.currentServer}</span>
                                        )}
                                    </span>
                                </div>
                            )}
                            {getEstimatedRemaining(scanProgress) && (
                                <div style={{ fontSize: '12px', color: '#64748b', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    <i className="fa-regular fa-clock" style={{ color: '#94a3b8' }} />
                                    <span>Temps restant estimé : <strong style={{ color: '#1e293b' }}>{getEstimatedRemaining(scanProgress)}</strong></span>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* ── Controls ── */}
                <div className="sg-card" style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'center' }}>
                    {/* Search */}
                    <div style={{ position: 'relative', flex: '1', minWidth: '200px' }}>
                        <i className="fa-regular fa-magnifying-glass" style={{
                            position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8',
                        }} />
                        <input
                            className="sg-filter-input"
                            style={{ width: '100%', paddingLeft: '36px', boxSizing: 'border-box' }}
                            type="text"
                            placeholder="Rechercher site, URL, message..."
                            value={searchQuery}
                            onChange={e => setSearchQuery(e.target.value)}
                        />
                    </div>

                    {/* Type filter */}
                    <select
                        className="sg-filter-input"
                        value={selectedType}
                        onChange={e => setSelectedType(e.target.value)}
                        style={{ minWidth: '180px' }}
                    >
                        <option value="">Tous les types</option>
                        {allTypes.map(t => (
                            <option key={t} value={t}>{t}</option>
                        ))}
                    </select>

                    {/* Refresh */}
                    <button className="sg-btn-secondary" onClick={fetchErrors} disabled={loading}>
                        <i className="fa-regular fa-rotate-right mr-10" />
                        Rafraîchir
                    </button>

                    {/* Manual scan */}
                    <button
                        className="sg-btn-primary"
                        onClick={handleTriggerScan}
                        disabled={scanRunning}
                        style={{ background: scanRunning ? '#94a3b8' : 'linear-gradient(135deg,#e74c3c,#c0392b)' }}
                    >
                        {scanRunning
                            ? <><i className="fa-solid fa-spinner fa-spin" /> Scan lancé...</>
                            : <><i className="fa-solid fa-radar" /> Lancer un scan</>
                        }
                    </button>
                </div>

                {scanToast && (
                    <div className={scanToast.type === 'success' ? 'sg-toast-success' : 'sg-toast-error'}>
                        {scanToast.msg}
                    </div>
                )}

                {/* ── Check URL ── */}
                <div className="sg-card">
                    <h4 style={{ margin: '0 0 12px', fontSize: '14px', fontWeight: 700, color: '#374151', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <i className="fa-regular fa-magnifying-glass-chart" style={{ color: '#3b82f6' }} />
                        Analyser une URL manuellement
                        <div className="sg-tooltip-container">
                            <i className="fa-solid fa-circle-info" style={{ color: '#94a3b8', cursor: 'help', fontSize: '13px' }} />
                            <div className="sg-tooltip">
                                <strong>Types d'URLs acceptés :</strong>
                                <ul style={{ margin: '5px 0 0', paddingLeft: '15px', listStyleType: 'disc' }}>
                                    <li>URL racine (ex: https://site.ch)</li>
                                    <li>Page spécifique (ex: https://site.ch/contact)</li>
                                    <li>N'importe quel site WordPress externe</li>
                                </ul>
                                <p style={{ margin: '5px 0 0', fontSize: '11px', opacity: 0.9 }}>
                                    L'analyse vérifie les erreurs PHP visibles, la structure HTML, les images cassées et les balises SEO.
                                </p>
                            </div>
                        </div>
                    </h4>

                    <style>{`
                        .sg-tooltip-container {
                            position: relative;
                            display: inline-flex;
                            align-items: center;
                        }
                        .sg-tooltip {
                            visibility: hidden;
                            width: 260px;
                            background-color: #1e293b;
                            color: #fff;
                            text-align: left;
                            border-radius: 8px;
                            padding: 12px;
                            position: absolute;
                            z-index: 100;
                            bottom: 125%;
                            left: 50%;
                            transform: translateX(-50%);
                            opacity: 0;
                            transition: opacity 0.3s;
                            font-size: 12px;
                            font-weight: 400;
                            line-height: 1.4;
                            box-shadow: 0 10px 15px -3px rgba(0,0,0,0.1);
                        }
                        .sg-tooltip::after {
                            content: "";
                            position: absolute;
                            top: 100%;
                            left: 50%;
                            margin-left: -5px;
                            border-width: 5px;
                            border-style: solid;
                            border-color: #1e293b transparent transparent transparent;
                        }
                        .sg-tooltip-container:hover .sg-tooltip {
                            visibility: visible;
                            opacity: 1;
                        }
                    `}</style>
                    <form onSubmit={handleCheckUrl} style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                        <input
                            className="sg-filter-input"
                            style={{ flex: 1, minWidth: '260px' }}
                            type="url"
                            placeholder="https://exemple.ch"
                            value={checkUrl}
                            onChange={e => setCheckUrl(e.target.value)}
                        />
                        <button type="submit" className="sg-btn-primary" disabled={checkLoading}>
                            {checkLoading
                                ? <><i className="fa-solid fa-spinner fa-spin" /> Analyse...</>
                                : <><i className="fa-solid fa-play" /> Analyser</>
                            }
                        </button>
                    </form>

                    {checkResult && (
                        <div style={{
                            marginTop: '14px',
                            padding: '14px',
                            borderRadius: '10px',
                            background: checkResult.status === 'OK' ? '#f0fdf4' : '#fef2f2',
                            border: `1px solid ${checkResult.status === 'OK' ? '#86efac' : '#fca5a5'}`,
                        }}>
                            {checkResult.status === 'OK' ? (
                                <div style={{ color: '#15803d', fontWeight: 600, fontSize: '14px' }}>
                                    <i className="fa-solid fa-circle-check" style={{ marginRight: '8px' }} />
                                    Aucune erreur détectée sur cette page.
                                </div>
                            ) : (
                                <div>
                                    <p style={{ color: '#b91c1c', fontWeight: 700, marginBottom: '8px', fontSize: '14px' }}>
                                        <i className="fa-solid fa-triangle-exclamation" style={{ marginRight: '8px' }} />
                                        {checkResult.errors?.length || 0} problème(s) détecté(s) :
                                    </p>
                                    <ul style={{ margin: 0, paddingLeft: '20px', color: '#374151', fontSize: '13px' }}>
                                        {(checkResult.errors || []).map((e, i) => (
                                            <li key={i} style={{ marginBottom: '4px' }}
                                                dangerouslySetInnerHTML={{ __html: e }} />
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* ── Error List ── */}
                <div className="sg-card">
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                        <h4 style={{ margin: 0, fontSize: '15px', fontWeight: 700, color: '#1e293b' }}>
                            <i className="fa-solid fa-circle-exclamation" style={{ color: '#e74c3c', marginRight: '8px' }} />
                            Erreurs actives :
                            <span style={{
                                marginLeft: '10px', background: '#e74c3c', color: '#fff',
                                borderRadius: '20px', padding: '2px 10px', fontSize: '12px', fontWeight: 700,
                            }}>
                                {totalErrors}
                            </span>
                            <span style={{
                                marginLeft: '10px', background: '#3b82f6', color: '#fff',
                                borderRadius: '20px', padding: '2px 10px', fontSize: '12px', fontWeight: 700,
                            }}>
                                {filteredData.length} sites
                            </span>
                        </h4>
                        {searchQuery || selectedType ? (
                            <button className="sg-btn-secondary" style={{ fontSize: '12px', padding: '6px 12px' }}
                                onClick={() => { setSearchQuery(''); setSelectedType(''); }}>
                                <i className="fa-regular fa-xmark" /> Réinitialiser filtres
                            </button>
                        ) : null}
                    </div>

                    {loading ? (
                        <div style={{ textAlign: 'center', padding: '40px', color: '#94a3b8' }}>
                            <i className="fa-solid fa-spinner fa-spin" style={{ fontSize: '24px', marginBottom: '10px', display: 'block' }} />
                            Chargement des erreurs...
                        </div>
                    ) : error ? (
                        <div style={{ textAlign: 'center', padding: '30px', color: '#ef4444' }}>
                            <i className="fa-solid fa-triangle-exclamation" style={{ marginRight: '8px' }} />
                            {error}
                        </div>
                    ) : filteredData.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
                            <i className="fa-solid fa-circle-check" style={{ fontSize: '32px', color: '#22c55e', marginBottom: '12px', display: 'block' }} />
                            <strong style={{ fontSize: '16px' }}>Aucune erreur active</strong>
                            <p style={{ margin: '6px 0 0', fontSize: '13px' }}>
                                {searchQuery || selectedType ? 'Aucun résultat pour ces filtres.' : 'Tous les sites sont en bonne santé ! Le prochain scan aura lieu automatiquement.'}
                            </p>
                        </div>
                    ) : (
                        <div>
                            {filteredData.map((site, i) => (
                                <SiteAccordion key={i} site={site} onValidate={handleValidate} />
                            ))}
                        </div>
                    )}
                </div>

            </div>
        </Fragment>
    );
};

export default SitesGestion;
